/**
 * What a probed slot becomes once the operator keeps it: a `Camera` and its single `View`.
 *
 * Shared by the two flows that turn a screenshot into rows — `DeviceDiscoveryPanel` (probing slots
 * on an existing capture device) and `BulkCaptureDeviceForm` (probing hosts on a subnet). The
 * `url_mode` rule in particular must exist once: `load_store_configs` re-derives it server-side and
 * reports a mismatch as drift, so two copies would drift silently.
 */

const SLOT_PLACEHOLDER = "{}";

/**
 * Brand kinds for which chauffeur reads `rtsp_url_suffix` (`rtsp.py::build_rtsp_url`); every other
 * kind builds its URL from its own DVR class.
 */
export const SUFFIX_KINDS = ["IP", "HTTP"];

/**
 * `url_mode` for a discovered camera, mirroring `conversion/helpers/brand.py::infer_url_mode`.
 *
 * A suffix always wins → EXPLICIT. Failing that, the brands outside `_TEMPLATED_KINDS` — the generic
 * IP/HTTP family, i.e. exactly `SUFFIX_KINDS` — have no template for Simone to render, so they are
 * EXPLICIT even with an empty suffix. An unresolved kind falls back to TEMPLATED, the column
 * default, rather than guessing.
 *
 * @param {string} literalSuffix - The expanded suffix, empty when none was given.
 * @param {string|null|undefined} brandKind - The `RecorderBrand.kind` of the device's brand.
 * @returns {"EXPLICIT"|"TEMPLATED"} The mode to persist.
 */
export function inferUrlMode(literalSuffix, brandKind) {
  if (literalSuffix) return "EXPLICIT";
  if (brandKind == null || brandKind === "") return "TEMPLATED";
  return SUFFIX_KINDS.includes(String(brandKind).toUpperCase()) ? "EXPLICIT" : "TEMPLATED";
}

/**
 * Expand the `{}` placeholder with a slot number.
 *
 * The suffix is stored on the camera as a literal: the CheckConstraint
 * `camera_rtsp_url_suffix_no_curly_placeholder` rejects a persisted `{}`.
 *
 * @param {string} suffix - The suffix template.
 * @param {number} slot - The slot number to inject.
 * @returns {string} The expanded suffix, `""` when there was none.
 */
export function expandSuffix(suffix, slot) {
  return String(suffix ?? "")
    .split(SLOT_PLACEHOLDER)
    .join(String(slot));
}

/**
 * Build the camera (plus its single view) to create for a slot that answered a probe.
 *
 * `inference_machine` is only persisted when the caller can name the machine the probe went through.
 * The device-scoped panel cannot: under its store fallback the machine belongs to *another* device, so
 * it stays null (legacy parity — a scan-created channel carried only recorder + number +
 * `is_active: false`) and an operator assigns it. A store-scoped probe can: the operator picked or
 * confirmed that machine and the frame proves it reaches the camera.
 *
 * @param {object} args - The probe's outcome and parameters.
 * @param {number} args.slot - The slot that answered.
 * @param {string} args.suffix - The suffix probed with, possibly templated.
 * @param {string} args.streamQuality - `MAIN` or `SUB`, as probed.
 * @param {string} args.protocol - `TCP` or `UDP`, as picked by the operator.
 * @param {string|null|undefined} args.brandKind - The brand kind the probe used.
 * @param {string} args.publicId - The camera's TEMP public id.
 * @param {string} args.viewPublicId - The view's TEMP public id.
 * @param {number|null} [args.inferenceMachine] - The machine the probe was routed through, when the
 *   caller can vouch for it; omitted persists null.
 * @returns {object} A camera shaped for `buildChangeset`.
 */
export default function buildDiscoveredCamera({
  slot,
  suffix,
  streamQuality,
  protocol,
  brandKind,
  publicId,
  viewPublicId,
  inferenceMachine = null,
}) {
  const literalSuffix = suffix ? expandSuffix(suffix, slot) : "";
  return {
    public_id: publicId,
    slot_number: slot,
    stream_quality: streamQuality,
    rtsp_url_suffix: literalSuffix,
    url_mode: inferUrlMode(literalSuffix, brandKind),
    // The operator's pick, not a constant: one who chose UDP because TCP fails on this box would
    // otherwise get rows that cannot stream. The probe itself does not test it.
    protocol,
    // Never analysed before an operator has reviewed it: the CAMERA is created inactive. Its view is
    // active from the start, so activating the camera later is a single toggle (a camera must always
    // keep at least one active view anyway).
    is_active: false,
    // Spread, not assigned: `pickFields` drops `undefined` but keeps `null`, and an explicit null on a
    // create is the same as omitting it — only clearer to leave the key out entirely.
    ...(inferenceMachine == null ? {} : { inference_machine: inferenceMachine }),
    views: [{ public_id: viewPublicId, view_number: 1, is_active: true }],
  };
}
